/**
 * Property and Debt editors (spec §12). Debts show computed payoff date and
 * lifetime interest, with the extra-payment comparison inline.
 */
import type { DebtType, Plan, Property } from '../model/types';
import { amortize } from '../engine/amortization';
import { CurrencyField, NumberField } from './Inputs';
import { fmtCompactUsd, fmtUsd } from './format';

interface Props {
  plan: Plan;
  update: (mut: (p: Plan) => void) => void;
}

const PROPERTY_TYPE_LABELS: Record<Property['type'], string> = {
  primary: 'Primary home',
  secondary: 'Second home',
  rental: 'Rental',
  land: 'Land',
};

const DEBT_TYPE_LABELS: Record<DebtType, string> = {
  mortgage: 'Mortgage',
  heloc: 'HELOC',
  auto: 'Auto loan',
  student: 'Student loan',
  personal: 'Personal loan',
  credit_card: 'Credit card',
};

// ── Properties ──────────────────────────────────────────────────────────────

export function PropertyTab({ plan, update }: Props) {
  return (
    <div>
      {plan.properties.map((prop, i) => (
        <details key={prop.id} className="item-card">
          <summary>
            <strong>{prop.label || PROPERTY_TYPE_LABELS[prop.type]}</strong>
            <span className="sub">
              {PROPERTY_TYPE_LABELS[prop.type]} · {fmtCompactUsd(prop.currentValue)}
              {prop.plannedSaleYear ? ` · selling ${prop.plannedSaleYear}` : ''}
              {prop.purchaseYear && prop.purchaseYear > 2026 ? ` · buying ${prop.purchaseYear}` : ''}
            </span>
          </summary>
          <div className="inputs-grid">
            <div className="field">
              <label>
                Label
                <input value={prop.label}
                  onChange={(e) => update((p) => { p.properties[i]!.label = e.target.value; })} />
              </label>
            </div>
            <div className="field">
              <label>
                Type
                <select value={prop.type}
                  onChange={(e) => update((p) => { p.properties[i]!.type = e.target.value as Property['type']; })}>
                  {(Object.keys(PROPERTY_TYPE_LABELS) as Property['type'][]).map((t) => (
                    <option key={t} value={t}>{PROPERTY_TYPE_LABELS[t]}</option>
                  ))}
                </select>
              </label>
              {prop.type === 'primary' && (
                <span className="hint">
                  Sale applies the §121 exclusion ($250k single / $500k joint) — assumes you've
                  lived there 2 of the last 5 years
                </span>
              )}
              {prop.type === 'rental' && (
                <span className="hint">
                  Sale estimates depreciation recapture at 25% — a simplification; confirm with a CPA
                </span>
              )}
            </div>
            <CurrencyField label="Value today" value={prop.currentValue}
              onCommit={(v) => update((p) => { p.properties[i]!.currentValue = v; })} />
            <CurrencyField label="Cost basis (what you paid + improvements)" value={prop.costBasis}
              onCommit={(v) => update((p) => { p.properties[i]!.costBasis = v; })} />
            <NumberField label="Planned sale year (2100 = never)" min={2026} max={2100}
              value={prop.plannedSaleYear ?? 2100}
              onCommit={(v) => update((p) => { p.properties[i]!.plannedSaleYear = v === 2100 ? undefined : v; })} />
            <NumberField label="Purchase year (if future)" min={2020} max={2100}
              value={prop.purchaseYear ?? 2020}
              hint="2020 = already owned"
              onCommit={(v) => update((p) => { p.properties[i]!.purchaseYear = v <= 2026 ? undefined : v; })} />
            <NumberField label="Selling costs %" min={0} max={12}
              value={Math.round(prop.sellingCostPct * 100)}
              hint="Agent commissions + closing, typically 6-8%"
              onCommit={(v) => update((p) => { p.properties[i]!.sellingCostPct = v / 100; })} />
            <CurrencyField label="Property tax / yr" value={prop.propertyTaxAnnual}
              onCommit={(v) => update((p) => { p.properties[i]!.propertyTaxAnnual = v; })} />
            <CurrencyField label="Insurance / yr" value={prop.insuranceAnnual}
              onCommit={(v) => update((p) => { p.properties[i]!.insuranceAnnual = v; })} />
            <CurrencyField label="HOA / yr" value={prop.hoaAnnual}
              onCommit={(v) => update((p) => { p.properties[i]!.hoaAnnual = v; })} />
            <NumberField label="Maintenance % of value" min={0} max={5}
              value={Math.round(prop.maintenancePctOfValue * 100)}
              onCommit={(v) => update((p) => { p.properties[i]!.maintenancePctOfValue = v / 100; })} />
            <div className="field">
              <label>
                Linked mortgage
                <select value={prop.mortgageId ?? ''}
                  onChange={(e) => update((p) => { p.properties[i]!.mortgageId = e.target.value || undefined; })}>
                  <option value="">None</option>
                  {plan.debts.filter((d) => d.type === 'mortgage' || d.type === 'heloc').map((d) => (
                    <option key={d.id} value={d.id}>{d.label}</option>
                  ))}
                </select>
              </label>
              <span className="hint">Paid off automatically when the property sells</span>
            </div>
            {prop.type === 'rental' && (
              <>
                <CurrencyField label="Gross rent / yr"
                  value={prop.rentalIncome?.grossAnnual ?? 0}
                  onCommit={(v) => update((p) => {
                    const r = p.properties[i]!.rentalIncome ?? { grossAnnual: 0, vacancyPct: 0.08, expensesAnnual: 0 };
                    r.grossAnnual = v;
                    p.properties[i]!.rentalIncome = v > 0 ? r : undefined;
                  })} />
                <NumberField label="Vacancy %" min={0} max={50}
                  value={Math.round((prop.rentalIncome?.vacancyPct ?? 0.08) * 100)}
                  onCommit={(v) => update((p) => {
                    if (p.properties[i]!.rentalIncome) p.properties[i]!.rentalIncome!.vacancyPct = v / 100;
                  })} />
                <CurrencyField label="Operating expenses / yr"
                  value={prop.rentalIncome?.expensesAnnual ?? 0}
                  onCommit={(v) => update((p) => {
                    if (p.properties[i]!.rentalIncome) p.properties[i]!.rentalIncome!.expensesAnnual = v;
                  })} />
              </>
            )}
          </div>
          <button className="btn danger" onClick={() => update((p) => { p.properties.splice(i, 1); })}>
            Remove property
          </button>
        </details>
      ))}

      <div className="add-row">
        <button className="btn"
          onClick={() => update((p) => {
            p.properties.push({
              id: `prop_${p.properties.length + 1}`,
              label: p.properties.length === 0 ? 'Home' : 'Property',
              type: p.properties.length === 0 ? 'primary' : 'rental',
              ownership: 'own_outright',
              currentValue: 0, costBasis: 0, sellingCostPct: 0.07,
              propertyTaxAnnual: 0, insuranceAnnual: 0, hoaAnnual: 0,
              maintenancePctOfValue: 0.01,
            });
          })}>
          + Add property
        </button>
      </div>
      <p className="hint">
        To model <strong>downsizing</strong>: set a sale year on your current home, then add the
        smaller place with a purchase year in the same year. The engine sells, pays off the
        mortgage, applies the §121 exclusion, banks the difference, and swaps the carrying costs.
        Reverse mortgages aren't modeled yet.
      </p>
    </div>
  );
}

// ── Debts ───────────────────────────────────────────────────────────────────

export function DebtTab({ plan, update }: Props) {
  return (
    <div>
      {plan.debts.map((debt, i) => {
        const base = amortize(debt.balance, debt.annualRate, debt.monthlyPayment);
        const withExtra = debt.extraPrincipalMonthly
          ? amortize(debt.balance, debt.annualRate, debt.monthlyPayment + debt.extraPrincipalMonthly)
          : null;
        const payoffText = (r: typeof base) =>
          r.payoffMonths == null
            ? 'never (payment doesn’t cover interest)'
            : `${Math.floor(r.payoffMonths / 12)}y ${r.payoffMonths % 12}m`;
        return (
          <details key={debt.id} className="item-card">
            <summary>
              <strong>{debt.label || DEBT_TYPE_LABELS[debt.type]}</strong>
              <span className="sub">
                {DEBT_TYPE_LABELS[debt.type]} · {fmtCompactUsd(debt.balance)} at{' '}
                {(debt.annualRate * 100).toFixed(2)}% · payoff {payoffText(base)}
              </span>
            </summary>
            <div className="inputs-grid">
              <div className="field">
                <label>
                  Label
                  <input value={debt.label}
                    onChange={(e) => update((p) => { p.debts[i]!.label = e.target.value; })} />
                </label>
              </div>
              <div className="field">
                <label>
                  Type
                  <select value={debt.type}
                    onChange={(e) => update((p) => { p.debts[i]!.type = e.target.value as DebtType; })}>
                    {(Object.keys(DEBT_TYPE_LABELS) as DebtType[]).map((t) => (
                      <option key={t} value={t}>{DEBT_TYPE_LABELS[t]}</option>
                    ))}
                  </select>
                </label>
                {(debt.type === 'mortgage' || debt.type === 'heloc') && (
                  <span className="hint">Interest feeds the itemized-deduction calculation</span>
                )}
              </div>
              <CurrencyField label="Balance" value={debt.balance}
                onCommit={(v) => update((p) => { p.debts[i]!.balance = v; })} />
              <NumberField label="Rate % (APR)" min={0} max={30}
                value={Math.round(debt.annualRate * 10000) / 100}
                onCommit={(v) => update((p) => { p.debts[i]!.annualRate = v / 100; })} />
              <CurrencyField label="Monthly payment" value={debt.monthlyPayment}
                onCommit={(v) => update((p) => { p.debts[i]!.monthlyPayment = v; })} />
              <CurrencyField label="Extra principal / month" value={debt.extraPrincipalMonthly ?? 0}
                onCommit={(v) => update((p) => { p.debts[i]!.extraPrincipalMonthly = v || undefined; })} />
            </div>
            <div style={{ padding: '0 var(--space-4) var(--space-3)', fontSize: 'var(--fs-small)', color: 'var(--ink-secondary)' }}>
              {base.payoffMonths != null && (
                <>
                  Lifetime interest at the current payment: <strong>{fmtUsd(base.totalInterest)}</strong>
                  {withExtra && withExtra.payoffMonths != null && (
                    <>
                      {' '}· with the extra payment: paid off{' '}
                      <strong>{Math.round((base.payoffMonths - withExtra.payoffMonths) / 12 * 10) / 10} years sooner</strong>,
                      saving <strong>{fmtUsd(base.totalInterest - withExtra.totalInterest)}</strong> in interest
                    </>
                  )}
                </>
              )}
            </div>
            <button className="btn danger" onClick={() => update((p) => { p.debts.splice(i, 1); })}>
              Remove debt
            </button>
          </details>
        );
      })}

      <div className="add-row">
        <button className="btn"
          onClick={() => update((p) => {
            p.debts.push({
              id: `debt_${p.debts.length + 1}`,
              label: 'Mortgage', type: 'mortgage',
              balance: 0, annualRate: 0.06, monthlyPayment: 0,
            });
          })}>
          + Add debt
        </button>
      </div>
      <p className="hint">
        Payments stop automatically when a balance hits zero — and the freed-up cash flow shows
        up in the projection, which is often the difference between a plan working and not.
      </p>
    </div>
  );
}
